import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Alert } from 'crux';
import get from 'lodash/get';
import ScrollToTop from '../../ScrollToTop';
import {
    isJoinDateProduct,
    isMemberDetailsSubsection,
    isIoBlockingOwnerSection,
    getMemberDetailsIoBlockingTarget,
} from '../../redux/selector/memberDetails';
import { normalizeMemberDetailsIoStatus } from '../../redux/utils/memberDetailsIoStatus';

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 0 12px 0;
`;

const MemberDetailsAlerts = ({
    isMemberDetails,
    questionList,
    memberDetailsIo,
    isJoinDate,
    isIoBlocking,
    requestOffersStatus,
    requestOffersMessage,
    ioDataUpdateStatus,
    ioDataUpdateMessage,
    uspsValidationStatus,
    uspsMessage,
    errorMessageColor,
    errorFontSize,
}) => {
    if (!isMemberDetails || !isJoinDate || !isIoBlocking) return null;

    const section = { questionList };
    if (!isIoBlockingOwnerSection(section, memberDetailsIo)) return null;

    const blockingTarget = getMemberDetailsIoBlockingTarget(memberDetailsIo);
    const uspsStatus = normalizeMemberDetailsIoStatus(uspsValidationStatus);
    const uspsMessageText = String(uspsMessage ?? '').trim();
    const showUsps = uspsStatus === 'blocked' && uspsMessageText !== '';
    const showRequestOffersIo = blockingTarget === 'verify';

    if (!showUsps && !showRequestOffersIo) return null;

    const alerts = [];

    if (showUsps) {
        alerts.push({
            key: 'usps-error',
            type: 'error',
            message: uspsMessageText,
        });
    } else if (showRequestOffersIo) {
        if (requestOffersStatus?.toLowerCase() === 'no_offer' && requestOffersMessage) {
            alerts.push({
                key: 'no-offer',
                type: 'error',
                message: requestOffersMessage,
            });
        } else if (requestOffersStatus?.toLowerCase() === 'error' && requestOffersMessage) {
            alerts.push({
                key: 'request-offers-error',
                type: 'error',
                message: requestOffersMessage,
            });
        }

        if (ioDataUpdateStatus?.toLowerCase() === 'error' && ioDataUpdateMessage) {
            alerts.push({
                key: 'io-data-update-error',
                type: 'error',
                message: ioDataUpdateMessage,
            });
        }
    }

    if (alerts.length === 0) return null;

    return (
        <ScrollToTop>
            <Stack>
                {alerts.map(a => (
                    <Alert
                        key={ a.key }
                        type={ a.type }
                        message={ a.message }
                        color={ errorMessageColor }
                        fontSize={ errorFontSize }
                    />
                ))}
            </Stack>
        </ScrollToTop>
    );
};

MemberDetailsAlerts.propTypes = {
    isMemberDetails: PropTypes.bool,
    questionList: PropTypes.arrayOf(PropTypes.object),
    memberDetailsIo: PropTypes.object,
    isJoinDate: PropTypes.bool,
    isIoBlocking: PropTypes.bool,
    requestOffersStatus: PropTypes.string,
    requestOffersMessage: PropTypes.string,
    ioDataUpdateStatus: PropTypes.string,
    ioDataUpdateMessage: PropTypes.string,
    uspsValidationStatus: PropTypes.string,
    uspsMessage: PropTypes.string,
    errorMessageColor: PropTypes.string,
    errorFontSize: PropTypes.string,
};

MemberDetailsAlerts.defaultProps = {
    isMemberDetails: false,
    questionList: [],
    memberDetailsIo: null,
    isJoinDate: false,
    isIoBlocking: false,
    requestOffersStatus: null,
    requestOffersMessage: null,
    ioDataUpdateStatus: null,
    ioDataUpdateMessage: null,
    uspsValidationStatus: null,
    uspsMessage: null,
    errorMessageColor: undefined,
    errorFontSize: undefined,
};

const mapStateToProps = (state, ownProps) => {
    const activeSubsectionId = get(state, 'questions.activeSubsectionId', '');
    const isMemberDetails = isMemberDetailsSubsection(activeSubsectionId);
    const memberDetailsIo = get(state, 'questions.otherParams.member_details_io', null);
    const isIoBlocking = isMemberDetails && !!memberDetailsIo?.blocking;
    return {
        isMemberDetails,
        questionList: get(ownProps, 'questionList', []),
        memberDetailsIo,
        isJoinDate: isJoinDateProduct(state),
        isIoBlocking,
        requestOffersStatus:  get(state, 'memberDetails.requestOffers.status'),
        requestOffersMessage: get(state, 'memberDetails.requestOffers.message'),
        ioDataUpdateStatus:   get(state, 'memberDetails.ioDataUpdate.status'),
        ioDataUpdateMessage:  get(state, 'memberDetails.ioDataUpdate.message'),
        uspsValidationStatus: get(state, 'memberDetails.uspsValidation.status'),
        uspsMessage:          get(state, 'memberDetails.uspsValidation.message'),
    };
};

export default connect(mapStateToProps, null)(MemberDetailsAlerts);
